import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Bell, AlertTriangle, Info, X, ChevronLeft, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AnnouncementPopup() {
  const { user, token } = useContext(AuthContext);
  const [announcements, setAnnouncements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (user && token) {
      fetchUnreadAnnouncements();
    }
  }, [user, token]);

  const fetchUnreadAnnouncements = async () => {
    try {
      const response = await axios.get(`${API}/api/announcements/unread`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && response.data.length > 0) {
        setAnnouncements(response.data);
        setCurrentIndex(0);
        setOpen(true);
      }
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
    }
  };

  const acknowledgeAndClose = async () => {
    if (announcements.length === 0) return;
    
    const currentAnnouncement = announcements[currentIndex];
    
    try {
      await axios.post(`${API}/api/announcements/${currentAnnouncement.id}/acknowledge`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // If there are more announcements, show the next one
      if (currentIndex < announcements.length - 1) {
        setCurrentIndex(currentIndex + 1);
      } else {
        // All announcements acknowledged
        setOpen(false);
        setAnnouncements([]);
      }
    } catch (error) {
      console.error('Failed to acknowledge announcement:', error);
      // Still close the popup even if acknowledgment fails
      setOpen(false);
    }
  };

  const acknowledgeAll = async () => {
    try {
      // Acknowledge all remaining announcements
      for (let i = currentIndex; i < announcements.length; i++) {
        await axios.post(`${API}/api/announcements/${announcements[i].id}/acknowledge`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setOpen(false);
      setAnnouncements([]);
    } catch (error) {
      console.error('Failed to acknowledge announcements:', error);
      setOpen(false);
    }
  };

  if (!open || announcements.length === 0) return null;

  const currentAnnouncement = announcements[currentIndex];
  const priorityConfig = {
    urgent: { 
      icon: AlertTriangle, 
      color: 'bg-red-600', 
      badgeColor: 'bg-red-100 text-red-800',
      borderColor: 'border-red-500'
    },
    important: { 
      icon: Bell, 
      color: 'bg-amber-600', 
      badgeColor: 'bg-amber-100 text-amber-800',
      borderColor: 'border-amber-500'
    },
    normal: { 
      icon: Info, 
      color: 'bg-blue-600', 
      badgeColor: 'bg-blue-100 text-blue-800',
      borderColor: 'border-blue-500'
    }
  };

  const config = priorityConfig[currentAnnouncement.priority] || priorityConfig.normal;
  const IconComponent = config.icon;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) acknowledgeAndClose(); }}>
      <DialogContent className={`sm:max-w-lg border-t-4 ${config.borderColor} grid-rows-[auto_minmax(0,1fr)_auto] max-h-[90vh]`}>
        <DialogHeader>
          <div className="flex items-center gap-3 pr-8">
            <div className={`p-2 rounded-full ${config.color}`}>
              <IconComponent className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <DialogTitle className="text-xl">{currentAnnouncement.title}</DialogTitle>
                <Badge className={config.badgeColor}>
                  {currentAnnouncement.priority === 'urgent' ? '🚨 Urgent' : 
                   currentAnnouncement.priority === 'important' ? '⚠️ Important' : 'ℹ️ Info'}
                </Badge>
              </div>
              <DialogDescription className="text-xs text-slate-500">
                Posted {new Date(currentAnnouncement.created_at).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        
        <div className="py-4 overflow-y-auto min-h-0" data-testid="announcement-message-scroll">
          <div className="prose prose-sm max-w-none">
            <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">
              {currentAnnouncement.message}
            </p>
          </div>

          {/* Pagination indicator (kept inside scroll area so it doesn't push the footer off-screen) */}
          {announcements.length > 1 && (
            <div className="flex items-center justify-center gap-2 py-2 mt-4 border-t">
              <span className="text-sm text-slate-500">
                Announcement {currentIndex + 1} of {announcements.length}
              </span>
              <div className="flex gap-1 ml-2">
                {announcements.map((_, idx) => (
                  <div 
                    key={idx} 
                    className={`w-2 h-2 rounded-full transition-colors ${
                      idx === currentIndex ? 'bg-blue-600' : 
                      idx < currentIndex ? 'bg-green-500' : 'bg-slate-300'
                    }`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2 sm:gap-2 border-t pt-4">
          {announcements.length > 1 && currentIndex < announcements.length - 1 && (
            <Button variant="outline" onClick={acknowledgeAll} className="flex-1 sm:flex-none" data-testid="announcement-dismiss-all-btn">
              <X className="w-4 h-4 mr-2" />
              Dismiss All ({announcements.length - currentIndex})
            </Button>
          )}
          <Button onClick={acknowledgeAndClose} className={`flex-1 sm:flex-none ${config.color} hover:opacity-90`} data-testid="announcement-got-it-btn">
            {currentIndex < announcements.length - 1 ? (
              <>
                Got it, Next
                <ChevronRight className="w-4 h-4 ml-2" />
              </>
            ) : (
              'Got it!'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
